import { useState, useContext, useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import {
  SimpleGrid,
  Box,
  Collapse,
  Button,
  Icon,
  useColorModeValue,
  Flex,
  FormLabel,
  FormControl,
  Input,
  Text,
} from '@chakra-ui/react';

import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import FormInput from 'components/fields/auto/FormInput';
import SelectInput from 'components/fields/auto/SelectInput';
import DateInput from 'components/fields/auto/DateInput';
import RadioInput from 'components/fields/auto/RadioInput';
import Message from 'components/fields/auto/Message';
import {
  Validation006Resolver,
  Validation023aResolver,
  Validation032Resolver,
  Validation034Resolver,
  Validation090aResolver,
  Validation090bResolver,
  Validation133bResolver,
} from 'components/fields/auto/resolvers';
import getValidationMessage from 'helpers/getValidationMessage';
import formatEmail from 'helpers/formatEmail';
import isValidEmail from 'helpers/isValidEmail';

import {
  FILE_TYPE,
  PROVINCE_ALBERTA,
  PROVINCE_NOVA_SCOTIA,
  PROVINCE_NEW_BRUNSWICK,
  PROVINCE_ONTARIO,
  isAtlanticProvince,
  isNonOntarioProvince,
} from 'variables/constants';
import { SECTIONS } from 'views/auto_form/constants';

import getDateFromString from 'helpers/getDateFromString';
import getRadioYesNo from 'helpers/getRadioYesNo';

import getDateString from 'helpers/getDateString';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import {
  addMissingMandatoryField,
  addOrUpdateRemark,
  changeDriver,
  changeDriverVehiclePercentageUse,
  removeMissingMandatoryField,
} from 'reducers/autoPersonalFormReducer';

import { APIPersonalAutoApplicationFormDriver } from 'types/api.types';
import type { RemarkInputProps } from 'components/fields/shared/Message/Message.types';

import Claims from '../Claims';
import Convictions from '../Convictions';

const parseTelematicsContactRemark = (text: string) => {
  const email = text.match(/^Email:\s*([^\n]*)/im)?.[1]?.trim() || '';
  const phone =
    text.match(/^Phone(?: Number)?:\s*([^\n]*)/im)?.[1]?.trim() || '';

  return { email, phone };
};

const getTelematicsContactRemarkText = (email: string, phone: string) =>
  `Email: ${email.trim()}\nPhone Number: ${phone.trim()}`;

const TelematicsContactRemarkInput = ({
  driverIndex,
  remark,
  onUpdateRemark,
}: RemarkInputProps & { driverIndex: number }) => {
  const parsed = parseTelematicsContactRemark(remark.text || '');
  const [email, setEmail] = useState(parsed.email);
  const [phone, setPhone] = useState(parsed.phone);
  const lastPersistedText = useRef(remark.text || '');

  useEffect(() => {
    const remarkText = remark.text || '';
    if (remarkText === lastPersistedText.current) return;

    const { email: savedEmail, phone: savedPhone } =
      parseTelematicsContactRemark(remarkText);
    setEmail(savedEmail);
    setPhone(savedPhone);
    lastPersistedText.current = remarkText;
  }, [remark.text]);

  const persistContact = (nextEmail: string, nextPhone: string) => {
    const hasEmail = !!nextEmail.trim();
    const hasPhone = !!nextPhone.trim();
    const hasValidEmail = hasEmail && isValidEmail(nextEmail);
    const text =
      hasEmail || hasPhone
        ? getTelematicsContactRemarkText(nextEmail, nextPhone)
        : '';

    lastPersistedText.current = text;
    onUpdateRemark({
      text,
      status: hasValidEmail && hasPhone ? 'approved' : 'created',
    });
  };

  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
      <FormControl isRequired>
        <FormLabel fontSize="sm" fontWeight="semibold">
          Email Address (Driver {driverIndex})
        </FormLabel>
        <Input
          type="email"
          value={email}
          placeholder="Enter driver's email address"
          onChange={(e) => {
            const nextEmail = formatEmail(e.target.value);
            setEmail(nextEmail);
            persistContact(nextEmail, phone);
          }}
          borderColor={email && !isValidEmail(email) ? 'red.500' : undefined}
        />
        {email && !isValidEmail(email) && (
          <Text color="red.500" fontSize="sm" mt={1}>
            Please enter a valid email address
          </Text>
        )}
      </FormControl>

      <FormControl isRequired>
        <FormLabel fontSize="sm" fontWeight="semibold">
          Cell Phone Number (Driver {driverIndex})
        </FormLabel>
        <Input
          value={phone}
          placeholder="Enter driver's cell phone number"
          onChange={(e) => {
            const nextPhone = e.target.value;
            setPhone(nextPhone);
            persistContact(email, nextPhone);
          }}
        />
      </FormControl>
    </SimpleGrid>
  );
};

const MARITAL_STATUS_OPTIONS = [
  { value: '', label: '' },
  { value: 'M', label: 'Married' },
  { value: 'S', label: 'Single' },
];

const SEX_OPTIONS = [
  { value: '', label: '' },
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
];

const PERCENTAGE_OPTIONS = [{ value: '', label: '' }].concat(
  Array.from({ length: 100 }, (_, i) => i + 1).map((i) => ({
    value: i.toString(),
    label: `${i}%`,
  })),
);

const Driver = ({
  driver,
  index,
  vehicle_count,
}: {
  driver: APIPersonalAutoApplicationFormDriver;
  index: number;
  vehicle_count: number;
  validation030?: any;
  validation034?: any;
}) => {
  const { validations } = useContext(ApplicationFormContext) as {
    validations: Validation[];
  };

  const [isCollapsed, setIsCollapsed] = useState(false);
  const dispatch = useAppDispatch();
  const isValidationResolversEnabled = useFeatureFlag(
    'enable-validation-resolvers',
  );
  const isRetireeDeclarationEsignEnabled = useFeatureFlag(
    'enable-retiree-declaration-esign',
  );
  const isExcludedDriverEsignEnabled = useFeatureFlag(
    'enable-excluded-driver-esign',
  );
  const isDlhRequired = useFeatureFlag('enable-dlh-required');

  const province = useAppSelector((state) => state.autoPersonalForm.province);
  const isOntario = province === PROVINCE_ONTARIO;
  const isAlberta = province === PROVINCE_ALBERTA;
  const isNovaScotia = province === PROVINCE_NOVA_SCOTIA;
  const isNewbrunswick = province === PROVINCE_NEW_BRUNSWICK;
  const isAtlantic = isAtlanticProvince(province);
  const isNewBrunswick = isAtlantic && !isNovaScotia;
  const isNonOntario = isNonOntarioProvince(province);

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('', 'gray.800');
  const shadowColor = useColorModeValue(
    '0 2px 4px rgba(0,0,0,0.1)',
    '0 2px 4px rgba(0,0,0,0.3)',
  );

  const validation023a = getValidationMessage(validations, '023a', index + 1);
  const validation023aItemKey = `driver-${index + 1}`;
  const validation023aObject = validations.find(
    (validation) => validation.validation_id === '023a',
  );
  const validation023aCheckType =
    validation023aObject?.validation_outcome_data?.checks_by_driver?.[
      String(index + 1)
    ];
  const hasValidation023a = !!validation023a;

  useEffect(() => {
    if (!hasValidation023a || validation023aCheckType !== 'b') return;

    dispatch(
      addOrUpdateRemark({
        validation_key: '023a',
        item_key: validation023aItemKey,
        data: {
          text: 'I confirm that the licensing dates shown on my application represent the original date(s) I was first licensed to drive in Ontario, as recorded by the Ministry of Transportation (MTO). I understand that inaccurate or incomplete disclosure of my licensing history may impact rating, eligibility, or coverage.',
          status: 'approved',
          text_validator_log_id: null,
          ai_result: '',
          section: SECTIONS.DRIVERS,
          custom_data: {
            type: 'date_first_licensed',
            out_of_province_answer: null,
            resolution_method: null,
          },
        },
      }),
    );
  }, [
    dispatch,
    hasValidation023a,
    validation023aCheckType,
    validation023aItemKey,
  ]);

  const validation030 = getValidationMessage(validations, '030', index + 1);
  const validation034 = getValidationMessage(validations, '034', index + 1);
  const validation032 = getValidationMessage(validations, '032', index + 1);
  const validation033 = getValidationMessage(validations, '033', index + 1);
  const validation025 = getValidationMessage(validations, '025', index + 1);
  const validation090 = getValidationMessage(validations, '090', index + 1);
  const validation090a = getValidationMessage(validations, '090a', index + 1);
  const validation090b = getValidationMessage(validations, '090b', index + 1);
  const validation090Or090aOr090b =
    validation090b || validation090a || validation090;
  const gapValidation = validation090b
    ? { validationId: '090b' as const, file: FILE_TYPE.TYPE_090b }
    : validation090a
      ? { validationId: '090a' as const, file: FILE_TYPE.TYPE_090a }
      : { validationId: '090' as const, file: FILE_TYPE.TYPE_090 };
  const validation108 = getValidationMessage(validations, '108', index + 1);
  const validation112 = getValidationMessage(validations, '112');
  const validation130 = getValidationMessage(validations, '130', index + 1);
  const validation125 = getValidationMessage(validations, '125', index + 1);
  const validation133 = getValidationMessage(validations, '133', index + 1);
  const validation133a = getValidationMessage(validations, '133a', index + 1);
  const validation133b = getValidationMessage(validations, '133b', index + 1);
  const dlhValidationId = validation133a ? ('133a' as const) : ('133' as const);
  const validation047 = getValidationMessage(validations, '047', index + 1);
  const validation101 = getValidationMessage(validations, '101', index + 1);
  const validation122 = getValidationMessage(validations, '122', index + 1);
  const validation007a = getValidationMessage(validations, '007a', index + 1);
  const validation007b = getValidationMessage(validations, '007b', index + 1);
  const autoplusValidation = validation007b
    ? { validationId: '007b' as const, file: FILE_TYPE.TYPE_007b }
    : validation007a
      ? { validationId: '007a' as const, file: FILE_TYPE.TYPE_007a }
      : { validationId: '007' as const, file: FILE_TYPE.TYPE_007 };

  const onDriverChange = (event: any) => {
    const { name, value, checked, type } = event.target;
    const payload = type === 'checkbox' ? checked : value;

    dispatch(
      changeDriver({
        name,
        index,
        value: payload,
      }),
    );
  };

  const onDateChange = (date: Date, name: string) => {
    dispatch(
      changeDriver({
        name,
        index,
        value: getDateString(date),
      }),
    );
  };

  const onMaritalStatusChange = (event: any) => {
    dispatch(
      changeDriver({
        name: 'marital_status',
        index,
        value: event.target.value,
      }),
    );
  };

  const onSexChange = (event: any) => {
    dispatch(
      changeDriver({
        name: 'sex',
        index,
        value: event.target.value,
      }),
    );
  };

  const onVehiclePercentageUseChange = (event: any, vehicle_id: number) => {
    dispatch(
      changeDriverVehiclePercentageUse({
        driver_index: index,
        vehicle_id,
        value: event.target.value,
      }),
    );
  };

  const retireeEmailMissingFieldName = `Retiree Email (Driver ${index + 1})`;
  const excludedDriverEmailMissingFieldName = `Excluded Driver Email (Driver ${index + 1})`;

  useEffect(() => {
    if (!isRetireeDeclarationEsignEnabled) return;

    if (isNonOntario) {
      dispatch(removeMissingMandatoryField(retireeEmailMissingFieldName));
      return;
    }

    const isRetiree = Boolean(driver.is_retiree_discount);
    const hasRetireeEmail = Boolean(driver.retiree_email?.trim());

    if (isRetiree && !hasRetireeEmail) {
      dispatch(
        addMissingMandatoryField({
          name: retireeEmailMissingFieldName,
          section: SECTIONS.DRIVERS,
        }),
      );
      return;
    }

    dispatch(removeMissingMandatoryField(retireeEmailMissingFieldName));
  }, [
    dispatch,
    driver.is_retiree_discount,
    driver.retiree_email,
    index,
    isNonOntario,
    isRetireeDeclarationEsignEnabled,
    retireeEmailMissingFieldName,
  ]);

  useEffect(() => {
    if (!isExcludedDriverEsignEnabled || isNonOntario) {
      dispatch(
        removeMissingMandatoryField(excludedDriverEmailMissingFieldName),
      );
      return;
    }

    const isExcluded = Boolean(driver.is_excluded_driver);
    const excludedDriverEmail = driver.excluded_driver_email?.trim() || '';
    const hasValidExcludedEmail =
      Boolean(excludedDriverEmail) && isValidEmail(excludedDriverEmail);

    if (isExcluded && !hasValidExcludedEmail) {
      dispatch(
        addMissingMandatoryField({
          name: excludedDriverEmailMissingFieldName,
          section: SECTIONS.DRIVERS,
        }),
      );
      return;
    }

    dispatch(removeMissingMandatoryField(excludedDriverEmailMissingFieldName));
  }, [
    dispatch,
    driver.is_excluded_driver,
    driver.excluded_driver_email,
    excludedDriverEmailMissingFieldName,
    isExcludedDriverEsignEnabled,
    isNonOntario,
  ]);

  const getDriverSummary = (driver: any) => {
    const nameParts = driver.name ? driver.name.split(' ') : [];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    const licenseNumber = driver.license || '';

    return `${firstName} ${lastName}${
      licenseNumber ? `, License: ${licenseNumber}` : ''
    }`;
  };

  return (
    <Box
      border="1px solid"
      borderColor={borderColor}
      borderRadius="md"
      bg={bgColor}
      boxShadow={shadowColor}
      overflow="hidden"
      mb="20px"
    >
      <Box
        rounded="5px"
        p="10px"
        backgroundColor="brand.400"
        color="white"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          <Box fontWeight="medium">
            {getDriverSummary(driver)} {`(Driver ${index + 1})`}
          </Box>
        </Box>
        <Button
          variant="ghost"
          size="sm"
          color="white"
          _hover={{ bg: 'whiteAlpha.200' }}
          onClick={() => setIsCollapsed(!isCollapsed)}
          rightIcon={
            <Icon as={isCollapsed ? ChevronDownIcon : ChevronUpIcon} />
          }
        >
          {isCollapsed ? 'Expand' : 'Collapse'}
        </Button>
      </Box>

      <Collapse in={!isCollapsed} animateOpacity>
        <Box p="6">
          <Box mb="6">
            <SimpleGrid columns={5} spacing="20px">
              <FormInput
                section={SECTIONS.DRIVERS}
                label="Name"
                name="name"
                value={driver.name}
                onChange={onDriverChange}
                subLabel={`Driver ${index + 1}`}
                message={{
                  validationId: autoplusValidation.validationId,
                  storageKey: 'driver',
                  index: index + 1,
                  file: autoplusValidation.file,
                  compact: true,
                  hideRemarks:
                    autoplusValidation.validationId === '007a' ||
                    autoplusValidation.validationId === '007b',
                }}
                infoText="Name as shown on Driver's Licence"
                required
                disabled
              />

              <FormInput
                section={SECTIONS.DRIVERS}
                label="Driver's Licence Number"
                name="license"
                value={driver.license}
                onChange={onDriverChange}
                subLabel={`Driver ${index + 1}`}
                required
                disabled
              />

              <DateInput
                section={SECTIONS.DRIVERS}
                label="Date of Birth"
                date={getDateFromString(driver.birth_date)}
                onChange={(date: Date) => onDateChange(date, 'birth_date')}
                subLabel={`Driver ${index + 1}`}
                required
                disabled
              />

              <SelectInput
                section={SECTIONS.DRIVERS}
                label="Sex"
                value={driver.sex}
                options={SEX_OPTIONS}
                onChange={onSexChange}
                disabled
              />

              <SelectInput
                section={SECTIONS.DRIVERS}
                label="Marital Status"
                value={driver.marital_status}
                options={MARITAL_STATUS_OPTIONS}
                onChange={onMaritalStatusChange}
                disabled
              />
            </SimpleGrid>
          </Box>

          <Box mb="6">
            <SimpleGrid columns={5} spacing="20px">
              {(isAlberta || isAtlantic) && (
                <FormInput
                  section={SECTIONS.DRIVERS}
                  label="Occupation"
                  name="occupation"
                  value={driver.occupation || ''}
                  onChange={onDriverChange}
                  subLabel={`Driver ${index + 1}`}
                  disabled={isNonOntario}
                  message={
                    isNewBrunswick
                      ? {
                          validationId: 'occupation_other',
                          manual: {
                            message:
                              'Please enter the specific occupation (it cannot be left as "Other").',
                            show:
                              (driver.occupation || '').trim().toLowerCase() ===
                              'other',
                          },
                        }
                      : undefined
                  }
                />
              )}

              {isAtlantic && (
                <>
                  <FormInput
                    section={SECTIONS.DRIVERS}
                    label="Name of Employer"
                    name="employer_name"
                    value={driver.employer_name || ''}
                    onChange={onDriverChange}
                    subLabel={`Driver ${index + 1}`}
                  />
                  <FormInput
                    section={SECTIONS.DRIVERS}
                    label="Address of Employer"
                    name="employer_address"
                    value={driver.employer_address || ''}
                    onChange={onDriverChange}
                    subLabel={`Driver ${index + 1}`}
                  />
                  <FormInput
                    section={SECTIONS.DRIVERS}
                    label="Date Hired"
                    name="date_hired"
                    value={driver.date_hired || ''}
                    onChange={onDriverChange}
                    subLabel={`Driver ${index + 1}`}
                  />
                </>
              )}

              {(isAlberta || isNewbrunswick) && (
                <>
                  <FormInput
                    section={SECTIONS.DRIVERS}
                    label="Grid Level"
                    name="grid_level"
                    value={driver.grid_level || ''}
                    onChange={onDriverChange}
                    subLabel={`Driver ${index + 1}`}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.DRIVERS}
                    label="Driver Type"
                    name="driver_type"
                    value={driver.driver_type || ''}
                    onChange={onDriverChange}
                    subLabel={`Driver ${index + 1}`}
                    infoText="Principal, Occasional, or Listed"
                    disabled
                  />
                </>
              )}

              {(isAlberta || isAtlantic) && (
                <FormInput
                  section={SECTIONS.DRIVERS}
                  label="Relationship to Applicant"
                  name="relationship_to_applicant"
                  value={driver.relationship_to_applicant || ''}
                  onChange={onDriverChange}
                  subLabel={`Driver ${index + 1}`}
                  infoText="e.g. Applicant, Spouse, Child, Parent"
                  disabled={isNonOntario}
                />
              )}

              {isOntario ? (
                <>
                  <DateInput
                    section={SECTIONS.DRIVERS}
                    label="G date"
                    date={getDateFromString(driver.g_date)}
                    onChange={(date: Date) => onDateChange(date, 'g_date')}
                    subLabel={`Driver ${index + 1}`}
                    message={
                      validation023a
                        ? undefined
                        : {
                            validationId: '023',
                            storageKey: 'driver',
                            index: index + 1,
                            compact: true,
                          }
                    }
                    disabled
                  />

                  <DateInput
                    section={SECTIONS.DRIVERS}
                    label="G1 date"
                    date={getDateFromString(driver.g1_date)}
                    onChange={(date: Date) => onDateChange(date, 'g1_date')}
                    subLabel={`Driver ${index + 1}`}
                    message={{
                      validationId: '024',
                      storageKey: 'driver',
                      index: index + 1,
                      compact: true,
                    }}
                    disabled
                  />

                  <DateInput
                    section={SECTIONS.DRIVERS}
                    label="G2 date"
                    date={getDateFromString(driver.g2_date)}
                    onChange={(date: Date) => onDateChange(date, 'g2_date')}
                    subLabel={`Driver ${index + 1}`}
                    disabled
                  />

                  {validation023aCheckType === 'c' && (
                    <FormControl isRequired>
                      <FormLabel
                        display="flex"
                        fontSize="sm"
                        fontWeight="bold"
                        _hover={{ cursor: 'pointer' }}
                        pb="10px"
                        m="0"
                      >
                        Date First Licensed
                      </FormLabel>
                      <Box pb="5px">Out of province licensing</Box>
                      <Validation023aResolver
                        section={SECTIONS.DRIVERS}
                        itemKey={validation023aItemKey}
                        compact
                      />
                    </FormControl>
                  )}
                </>
              ) : (
                <>
                  <FormInput
                    section={SECTIONS.DRIVERS}
                    label="License Class"
                    name="license_class"
                    value={driver.license_class || ''}
                    onChange={onDriverChange}
                    subLabel={`Driver ${index + 1}`}
                    disabled
                  />

                  <DateInput
                    section={SECTIONS.DRIVERS}
                    label="Date Licensed"
                    date={getDateFromString(driver.licensed_date)}
                    onChange={(date: Date) =>
                      onDateChange(date, 'licensed_date')
                    }
                    subLabel={`Driver ${index + 1}`}
                    disabled
                  />

                  <DateInput
                    section={SECTIONS.DRIVERS}
                    label="Driver Training Date Completed"
                    date={getDateFromString(driver.driver_training_date)}
                    onChange={(date: Date) =>
                      onDateChange(date, 'driver_training_date')
                    }
                    subLabel={`Driver ${index + 1}`}
                    disabled
                  />
                </>
              )}

              {!isNonOntario && (
                <Box id={retireeEmailMissingFieldName}>
                  <RadioInput
                    section={SECTIONS.DRIVERS}
                    label="Retiree Discount?"
                    subLabel={`Driver ${index + 1}`}
                    options={[
                      { value: 'true', label: 'Yes' },
                      { value: 'false', label: 'No' },
                    ]}
                    value={getRadioYesNo(driver.is_retiree_discount)}
                    onChange={(value) =>
                      onDriverChange({
                        target: {
                          name: 'is_retiree_discount',
                          value: value === 'true',
                        },
                      })
                    }
                    message={{
                      validationId: 'retiree_discount',
                      storageKey: 'driver',
                      index: index + 1,
                      ...(!isRetireeDeclarationEsignEnabled && {
                        file: FILE_TYPE.TYPE_RETIREE_DISCOUNT_FORM,
                      }),
                      manual: {
                        message: isRetireeDeclarationEsignEnabled
                          ? "A Retiree Declaration Form will be included in the binder package for e-signing. Please enter the retiree's email address below."
                          : 'Upload Retiree discount form',
                        show: driver.is_retiree_discount,
                      },
                      hideRemarks: true,
                      compact: true,
                      ...(isRetireeDeclarationEsignEnabled && {
                        compactLabel: 'Add email',
                        compactStatus: driver.retiree_email
                          ? 'success'
                          : 'warning',
                        modalExtraContent: (
                          <FormControl isRequired>
                            <FormLabel fontSize="sm" fontWeight="semibold">
                              Retiree Email (Driver {index + 1})
                            </FormLabel>
                            <Input
                              id={retireeEmailMissingFieldName}
                              type="email"
                              placeholder="Enter retiree's email address"
                              value={driver.retiree_email || ''}
                              onChange={(e) =>
                                onDriverChange({
                                  target: {
                                    name: 'retiree_email',
                                    value: e.target.value,
                                  },
                                })
                              }
                            />
                          </FormControl>
                        ),
                      }),
                    }}
                    infoText="Do driver qualify for Retiree Discount?"
                    disabled
                    stacked
                  />
                </Box>
              )}

              {!isNonOntario &&
                isExcludedDriverEsignEnabled &&
                driver.is_excluded_driver && (
                  <FormControl
                    isRequired
                    id={excludedDriverEmailMissingFieldName}
                  >
                    <FormLabel
                      fontSize="sm"
                      fontWeight="semibold"
                      display="flex"
                      alignItems="center"
                    >
                      Excluded driver
                    </FormLabel>
                    <Box pb="5px">OPCF 28A endorsement</Box>
                    <Message
                      section={SECTIONS.DRIVERS}
                      validationId="043c"
                      storageKey="driver"
                      index={index + 1}
                      compact
                      hideRemarks
                      compactLabel="Add email"
                      compactStatus={
                        driver.excluded_driver_email?.trim() &&
                        isValidEmail(driver.excluded_driver_email)
                          ? 'success'
                          : 'warning'
                      }
                      manual={{
                        message:
                          "An OPCF 28A endorsement form will be included in the binder package for e-signing. Please enter the excluded driver's email address below.",
                        show: true,
                      }}
                      modalExtraContent={
                        <FormControl isRequired>
                          <FormLabel fontSize="sm" fontWeight="semibold">
                            {excludedDriverEmailMissingFieldName}
                          </FormLabel>
                          <Input
                            id={excludedDriverEmailMissingFieldName}
                            type="email"
                            placeholder="Enter excluded driver's email address"
                            value={driver.excluded_driver_email || ''}
                            onChange={(e) =>
                              onDriverChange({
                                target: {
                                  name: 'excluded_driver_email',
                                  value: formatEmail(e.target.value),
                                },
                              })
                            }
                          />
                          {driver.excluded_driver_email &&
                            !isValidEmail(driver.excluded_driver_email) && (
                              <Text color="orange.500" fontSize="sm" mt={1}>
                                Please enter a valid email address
                              </Text>
                            )}
                        </FormControl>
                      }
                    />
                  </FormControl>
                )}

              <Flex flexFlow={'wrap'}>
                {[...Array(vehicle_count)].map((_, vehicleIndex) => {
                  return (
                    <Box
                      key={vehicleIndex}
                      w={vehicle_count > 1 ? '50%' : '100%'}
                      pr={(vehicleIndex + 1) % 2 !== 0 ? '10px' : ''}
                      mt={vehicleIndex > 1 ? '10px' : ''}
                    >
                      <SelectInput
                        section={SECTIONS.DRIVERS}
                        key={vehicleIndex}
                        label={`Auto. ${vehicleIndex + 1}`}
                        value={
                          driver.vehicle_percentage_use[
                            `vehicle_${vehicleIndex + 1}`
                          ]
                        }
                        options={PERCENTAGE_OPTIONS}
                        onChange={(e) =>
                          onVehiclePercentageUseChange(e, vehicleIndex + 1)
                        }
                        disabled
                      />
                    </Box>
                  );
                })}
              </Flex>
            </SimpleGrid>
          </Box>

          <Box>
            <SimpleGrid columns={5} spacing="20px">
              {isDlhRequired && driver.is_training_certificate_required ? (
                <FormControl isRequired>
                  <FormLabel
                    display="flex"
                    fontSize="sm"
                    fontWeight="bold"
                    pb="10px"
                    m="0"
                  >
                    Driver Training Certificate Required?
                  </FormLabel>
                  <Message
                    section={SECTIONS.DRIVERS}
                    validationId="driver_training_certificate"
                    storageKey="driver"
                    index={index + 1}
                    file={{
                      ...FILE_TYPE.TYPE_DRIVER_TRAINING_CERTIFICATE,
                      mandatory: true,
                    }}
                    manual={{
                      message: 'Please provide Training Certificate file',
                      show: true,
                    }}
                    hideRemarks
                    compact
                  />
                </FormControl>
              ) : (
                <RadioInput
                  section={SECTIONS.DRIVERS}
                  label={
                    isNovaScotia || isNewBrunswick
                      ? 'Certificate for driver training course?'
                      : isAlberta
                        ? 'Drivers Training Certificate?'
                        : 'Training Certificate?'
                  }
                  subLabel={`Driver ${index + 1}`}
                  options={[
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                  ]}
                  value={getRadioYesNo(driver.is_training_certificate_attached)}
                  onChange={(value) =>
                    onDriverChange({
                      target: {
                        name: 'is_training_certificate_attached',
                        value: value === 'true',
                      },
                    })
                  }
                  message={{
                    validationId: 'driver_training_certificate',
                    storageKey: 'driver',
                    index: index + 1,
                    file: FILE_TYPE.TYPE_DRIVER_TRAINING_CERTIFICATE,
                    manual: {
                      message: 'Please provide Training Certificate file',
                      show: driver.is_training_certificate_attached,
                    },
                    hideRemarks: true,
                    compact: true,
                  }}
                  infoText="Driver Training Certificate Attached?"
                  stacked
                  required
                />
              )}

              {validation030 && (
                <FormControl isRequired>
                  <FormLabel
                    display="flex"
                    fontSize="sm"
                    fontWeight="bold"
                    _hover={{ cursor: 'pointer' }}
                    pb="10px"
                    m="0"
                  >
                    Know your customer
                  </FormLabel>
                  <Box pb="5px">Autoplus mismatch</Box>
                  <Message
                    validationId="030"
                    section={SECTIONS.DRIVERS}
                    storageKey="driver"
                    index={index + 1}
                    compact
                  />
                </FormControl>
              )}

              {validation034 &&
                (isValidationResolversEnabled ? (
                  <FormControl isRequired>
                    <FormLabel
                      display="flex"
                      fontSize="sm"
                      fontWeight="bold"
                      _hover={{ cursor: 'pointer' }}
                      pb="10px"
                      m="0"
                    >
                      Lapses
                    </FormLabel>
                    <Box pb="5px">Lapses mismatch</Box>
                    <Validation034Resolver
                      section={SECTIONS.DRIVERS}
                      itemKey={`driver-${index + 1}`}
                      compact
                    />
                  </FormControl>
                ) : (
                  <FormControl isRequired>
                    <FormLabel
                      display="flex"
                      fontSize="sm"
                      fontWeight="bold"
                      _hover={{ cursor: 'pointer' }}
                      pb="10px"
                      m="0"
                    >
                      Lapses
                    </FormLabel>
                    <Box pb="5px">Lapses mismatch</Box>
                    <Message
                      section={SECTIONS.DRIVERS}
                      validationId="034"
                      file={FILE_TYPE.TYPE_034}
                      storageKey="driver"
                      index={index + 1}
                      compact
                    />
                  </FormControl>
                ))}

              {validation032 &&
                (isValidationResolversEnabled ? (
                  <FormControl isRequired>
                    <FormLabel
                      display="flex"
                      fontSize="sm"
                      fontWeight="bold"
                      _hover={{ cursor: 'pointer' }}
                      pb="10px"
                      m="0"
                    >
                      Claims
                    </FormLabel>
                    <Box pb="5px">Claims mismatch</Box>
                    <Validation032Resolver
                      section={SECTIONS.DRIVERS}
                      itemKey={`driver-${index + 1}`}
                      compact
                    />
                  </FormControl>
                ) : (
                  <FormControl isRequired>
                    <FormLabel
                      display="flex"
                      fontSize="sm"
                      fontWeight="bold"
                      _hover={{ cursor: 'pointer' }}
                      pb="10px"
                      m="0"
                    >
                      Claims
                    </FormLabel>
                    <Box pb="5px">Claims mismatch</Box>
                    <Message
                      section={SECTIONS.DRIVERS}
                      validationId="032"
                      storageKey="driver"
                      index={index + 1}
                      file={FILE_TYPE.TYPE_032}
                      compact
                    />
                  </FormControl>
                ))}

              {validation033 && (
                <FormControl isRequired>
                  <FormLabel
                    display="flex"
                    fontSize="sm"
                    fontWeight="bold"
                    _hover={{ cursor: 'pointer' }}
                    pb="10px"
                    m="0"
                  >
                    Convictions
                  </FormLabel>
                  <Box pb="5px">Convictions mismatch</Box>
                  <Message
                    section={SECTIONS.DRIVERS}
                    validationId="033"
                    storageKey="driver"
                    index={index + 1}
                    hideRemarks
                  />
                </FormControl>
              )}

              {validation025 && (
                <FormControl isRequired>
                  <FormLabel
                    display="flex"
                    fontSize="sm"
                    fontWeight="bold"
                    _hover={{ cursor: 'pointer' }}
                    pb="10px"
                    m="0"
                  >
                    Principal Driver
                  </FormLabel>
                  <Box pb="5px">Assigned to mutliple vehicles</Box>
                  <Message
                    section={SECTIONS.DRIVERS}
                    validationId="025"
                    storageKey="driver"
                    index={index + 1}
                    compact
                  />
                </FormControl>
              )}

              {validation090Or090aOr090b && (
                <FormControl isRequired>
                  <FormLabel
                    display="flex"
                    fontSize="sm"
                    fontWeight="bold"
                    _hover={{ cursor: 'pointer' }}
                    pb="10px"
                    m="0"
                  >
                    Gap in insurance
                  </FormLabel>
                  <Box pb="5px">Gap immediately prior</Box>
                  {validation090b ? (
                    <Validation090bResolver
                      section={SECTIONS.DRIVERS}
                      itemKey={`driver-${index + 1}`}
                      compact
                    />
                  ) : validation090a ? (
                    <Validation090aResolver
                      section={SECTIONS.DRIVERS}
                      itemKey={`driver-${index + 1}`}
                      compact
                    />
                  ) : (
                    <Message
                      section={SECTIONS.DRIVERS}
                      validationId={gapValidation.validationId}
                      storageKey="driver"
                      index={index + 1}
                      file={gapValidation.file}
                      compact
                    />
                  )}
                </FormControl>
              )}

              {validation108 && (
                <FormControl isRequired>
                  <FormLabel
                    display="flex"
                    fontSize="sm"
                    fontWeight="bold"
                    _hover={{ cursor: 'pointer' }}
                    pb="10px"
                    m="0"
                  >
                    Prior Policy
                  </FormLabel>
                  <Box pb="5px">Driver Removed from Prior Policy</Box>
                  <Message
                    section={SECTIONS.DRIVERS}
                    validationId="108"
                    storageKey="driver"
                    index={index + 1}
                    file={FILE_TYPE.TYPE_108}
                    compact
                  />
                </FormControl>
              )}

              {validation130 &&
                (isValidationResolversEnabled ? (
                  <FormControl isRequired>
                    <FormLabel
                      display="flex"
                      fontSize="sm"
                      fontWeight="bold"
                      _hover={{ cursor: 'pointer' }}
                      pb="10px"
                      m="0"
                    >
                      Open Claims
                    </FormLabel>
                    <Box pb="5px">Any New Business With An Open Claim</Box>
                    <Validation006Resolver
                      section={SECTIONS.DRIVERS}
                      itemKey={`driver-${index + 1}`}
                      validationId="130"
                      compact
                    />
                  </FormControl>
                ) : (
                  <FormControl isRequired>
                    <FormLabel
                      display="flex"
                      fontSize="sm"
                      fontWeight="bold"
                      _hover={{ cursor: 'pointer' }}
                      pb="10px"
                      m="0"
                    >
                      Open Claims
                    </FormLabel>
                    <Box pb="5px">Any New Business With An Open Claim</Box>
                    <Message
                      section={SECTIONS.DRIVERS}
                      validationId="130"
                      storageKey="driver"
                      index={index + 1}
                      file={FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO}
                      compact
                    />
                  </FormControl>
                ))}

              {validation125 && (
                <FormControl isRequired>
                  <FormLabel
                    display="flex"
                    fontSize="sm"
                    fontWeight="bold"
                    _hover={{ cursor: 'pointer' }}
                    pb="10px"
                    m="0"
                  >
                    Prior Insurance History
                  </FormLabel>
                  <Box pb="5px">Missing DASH</Box>
                  <Message
                    section={SECTIONS.DRIVERS}
                    validationId="125"
                    storageKey="driver"
                    index={index + 1}
                    file={FILE_TYPE.TYPE_125}
                    compact
                    hideRemarks
                  />
                </FormControl>
              )}

              {(validation133 || validation133a) && (
                <FormControl isRequired>
                  <FormLabel
                    display="flex"
                    fontSize="sm"
                    fontWeight="bold"
                    _hover={{ cursor: 'pointer' }}
                    pb="10px"
                    m="0"
                  >
                    {validation133a
                      ? 'Intact - DLH Requirement'
                      : 'Aviva/Intact - DLH Requirement'}
                  </FormLabel>
                  <Box pb="5px">License and Insurance Experience</Box>
                  <Message
                    section={SECTIONS.DRIVERS}
                    validationId={dlhValidationId}
                    storageKey="driver"
                    index={index + 1}
                    file={
                      validation133a ? FILE_TYPE.TYPE_133a : FILE_TYPE.TYPE_133
                    }
                    compact
                    hideRemarks
                  />
                </FormControl>
              )}

              {validation133b && (
                <FormControl isRequired>
                  <FormLabel
                    display="flex"
                    fontSize="sm"
                    fontWeight="bold"
                    _hover={{ cursor: 'pointer' }}
                    pb="10px"
                    m="0"
                  >
                    Aviva - License and Insurance Experience
                  </FormLabel>
                  <Box pb="5px">DLH Requirement</Box>
                  <Validation133bResolver
                    section={SECTIONS.DRIVERS}
                    itemKey={`driver-${index + 1}`}
                    compact
                  />
                </FormControl>
              )}

              {validation047 && (
                <FormControl isRequired>
                  <FormLabel
                    display="flex"
                    fontSize="sm"
                    fontWeight="bold"
                    _hover={{ cursor: 'pointer' }}
                    pb="10px"
                    m="0"
                  >
                    Aviva discount
                  </FormLabel>
                  <Box pb="5px">Graduated licensing discount</Box>
                  <Message
                    section={SECTIONS.DRIVERS}
                    validationId="047"
                    storageKey="driver"
                    index={index + 1}
                    file={FILE_TYPE.TYPE_047}
                    compact
                    hideRemarks
                  />
                </FormControl>
              )}

              {validation101 && (
                <FormControl isRequired>
                  <FormLabel
                    display="flex"
                    fontSize="sm"
                    fontWeight="bold"
                    _hover={{ cursor: 'pointer' }}
                    pb="10px"
                    m="0"
                  >
                    MVR
                  </FormLabel>
                  <Box pb="5px">License status</Box>
                  <Message
                    section={SECTIONS.DRIVERS}
                    validationId="101"
                    storageKey="driver"
                    index={index + 1}
                    file={FILE_TYPE.TYPE_101}
                    compact
                    hideRemarks
                  />
                </FormControl>
              )}

              {validation112 && (
                <FormControl isRequired>
                  <FormLabel
                    display="flex"
                    fontSize="sm"
                    fontWeight="bold"
                    _hover={{ cursor: 'pointer' }}
                    pb="10px"
                    m="0"
                  >
                    Telematics
                  </FormLabel>
                  <Box pb="5px">Driver contact information</Box>
                  <Message
                    section={SECTIONS.DRIVERS}
                    validationId="112"
                    storageKey="driver"
                    index={index + 1}
                    compact
                    compactLabel="Add contact"
                    manual={{ show: true, message: validation112 }}
                    renderRemarkInput={(props) => (
                      <TelematicsContactRemarkInput
                        {...props}
                        driverIndex={index + 1}
                      />
                    )}
                  />
                </FormControl>
              )}

              {validation122 &&
                (isValidationResolversEnabled ? (
                  <FormControl isRequired>
                    <FormLabel
                      display="flex"
                      fontSize="sm"
                      fontWeight="bold"
                      _hover={{ cursor: 'pointer' }}
                      pb="10px"
                      m="0"
                    >
                      One or more cancellations for non-payment
                    </FormLabel>
                    <Box pb="5px">Jevco</Box>
                    <Validation006Resolver
                      section={SECTIONS.DRIVERS}
                      itemKey={`driver-${index + 1}`}
                      validationId="122"
                      compact
                    />
                  </FormControl>
                ) : (
                  <FormControl isRequired>
                    <FormLabel
                      display="flex"
                      fontSize="sm"
                      fontWeight="bold"
                      _hover={{ cursor: 'pointer' }}
                      pb="10px"
                      m="0"
                    >
                      One or more cancellations for non-payment
                    </FormLabel>
                    <Box pb="5px">Jevco</Box>
                    <Message
                      section={SECTIONS.DRIVERS}
                      validationId="122"
                      storageKey="driver"
                      index={index + 1}
                      file={FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO}
                      compact
                    />
                  </FormControl>
                ))}
            </SimpleGrid>
          </Box>
        </Box>

        <Claims index={index} />

        <Convictions index={index} />
      </Collapse>
    </Box>
  );
};

export default Driver;
